import React, { useEffect } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useInViewAnimation } from '../hooks/useInViewAnimation';
import { caseStudyIndex } from '../data/caseStudyIndex';

// /case-studies: the long reads, on their own page. /work is the catalogue of
// things you can open; this is where the engineering behind a few of them is
// written down. Cards render from caseStudyIndex so this route stays light and
// the full prose loads only when a study is opened.

// Six-column track so any count lays out without a stranded card: the first
// row takes two wide cards when the remainder divides by three (5 = 2 + 3),
// otherwise everything is 3-up. Literal classes so Tailwind keeps them.
const spanFor = (index, count) => {
  const lead = count % 3 === 2 ? 2 : count % 3 === 1 && count > 3 ? 4 : 0;
  // On two-column tablets an odd last card takes the full row.
  const tablet = count % 2 === 1 && index === count - 1 ? 'md:col-span-2 ' : '';
  if (index < lead) return `${tablet}lg:col-span-3`;
  return `${tablet}lg:col-span-2`;
};

export const CaseStudiesPage = ({ onNavigateHome, onNavigateWork, onNavigateCaseStudy }) => {
  const { ref, isInView } = useInViewAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
    const previousTitle = document.title;
    document.title = 'Case studies — Brandon Crenshaw';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="case-studies-page">
      <div className="max-w-[1200px] mx-auto px-6 pt-10">
        <button
          type="button"
          onClick={onNavigateHome}
          className="inline-flex items-center gap-2 text-sm text-[#273C46] hover:text-[#051A24] transition-colors py-2"
        >
          <ArrowLeft className="w-4 h-4" /> Back home
        </button>
      </div>

      <header className="max-w-[1200px] mx-auto px-6 pt-8 pb-10">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#273C46]/70 mb-3">How they were built</p>
        <h1 className="text-4xl md:text-5xl text-[#051A24] mb-4">
          Case{' '}
          <span style={{ fontFamily: "'Playfair Display', serif" }} className="italic">
            studies
          </span>
        </h1>
        <p className="text-[#273C46] max-w-2xl text-lg">
          The problem, the architecture, the decisions that mattered, and the numbers I can point to. Where a
          number does not exist yet, the page says so.
        </p>
      </header>

      <section ref={ref} className="max-w-[1200px] mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 items-stretch">
          {caseStudyIndex.map((study, index) => (
            <button
              key={study.slug}
              type="button"
              onClick={() => onNavigateCaseStudy(study.slug)}
              className={`${spanFor(index, caseStudyIndex.length)} group text-left flex flex-col h-full bg-white border border-[#E0EBF0] rounded-2xl overflow-hidden hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:border-[#273C46]/30 transition-all duration-300 ${
                isInView ? 'animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: `${0.1 + index * 0.06}s` }}
              data-testid={`case-study-card-${study.slug}`}
            >
              <div className="aspect-[16/10] overflow-hidden bg-[#E0EBF0]">
                {study.image && (
                  <img
                    src={study.image}
                    alt=""
                    loading="lazy"
                    className={`w-full h-full transition-transform duration-500 group-hover:scale-105 ${
                      study.imageFit === 'contain' ? 'object-contain p-4 bg-white' : 'object-cover'
                    }`}
                  />
                )}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#273C46]/70 mb-3">{study.kicker}</p>
                <h2 className="text-xl text-[#051A24] mb-2 leading-snug">
                  {study.project}:{' '}
                  <span style={{ fontFamily: "'Playfair Display', serif" }} className="italic">
                    {study.title}
                  </span>
                </h2>
                <p className="text-sm text-[#273C46] leading-relaxed mb-5 flex-1">{study.summary}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#051A24] pt-4 border-t border-[#E0EBF0] group-hover:gap-2.5 transition-all">
                  Read it <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-12 text-sm text-[#273C46]">
          Looking for everything, not just the write-ups?{' '}
          <button
            type="button"
            onClick={onNavigateWork}
            className="font-medium text-[#051A24] underline underline-offset-4 decoration-[#E0EBF0] hover:decoration-[#051A24] min-h-[44px]"
          >
            See all projects
          </button>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
